import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, X, Star } from "lucide-react";
import api, { fmtErr } from "@/lib/api";

const TABS = ["Bookings", "Reviews", "Vehicles", "Packages", "Promotions"];
const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0F1418] px-4 py-2.5 text-sm text-white outline-none transition-colors focus:border-[#D4AF37]";

const STATUS_COLORS = {
  new: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  completed: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
};

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const load = useCallback(() => api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/bookings/${id}`, { status }).catch(() => {});
    load();
  };
  const remove = async (id) => {
    await api.delete(`/bookings/${id}`).catch(() => {});
    load();
  };

  if (bookings.length === 0)
    return <p data-testid="bookings-empty" className="py-10 text-center text-sm text-white/40">No booking requests yet. New website bookings appear here.</p>;

  return (
    <div data-testid="bookings-table" className="space-y-3">
      {bookings.map((b) => (
        <div key={b.id} data-testid="booking-row" className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#171F26] p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-medium">{b.name} <span className="text-white/40">· {b.phone}</span></p>
            <p className="mt-1 text-sm text-white/55">
              {b.destination} — {b.vehicle}, {b.days} day{b.days > 1 ? "s" : ""}, from {b.pickup} on {b.travel_date}
            </p>
            {b.notes && <p className="mt-1 text-xs text-white/40">"{b.notes}"</p>}
          </div>
          <div className="flex items-center gap-3">
            {b.payment_status === "paid" && (
              <span data-testid={`booking-paid-${b.id}`} className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-3 py-1.5 text-xs font-medium text-emerald-400">
                ₹{b.advance_amount || 500} advance paid
              </span>
            )}
            <select
              data-testid={`booking-status-${b.id}`}
              value={b.status}
              onChange={(e) => setStatus(b.id, e.target.value)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium outline-none ${STATUS_COLORS[b.status] || ""} bg-transparent`}
            >
              {Object.keys(STATUS_COLORS).map((s) => (
                <option key={s} value={s} className="bg-[#171F26]">{s}</option>
              ))}
            </select>
            <button data-testid={`booking-delete-${b.id}`} onClick={() => remove(b.id)} aria-label="Delete booking" className="text-white/30 transition-colors hover:text-red-400">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const load = useCallback(() => api.get("/reviews/all").then((r) => setReviews(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id, approved) => { await api.patch(`/reviews/${id}`, { approved }).catch(() => {}); load(); };
  const remove = async (id) => { await api.delete(`/reviews/${id}`).catch(() => {}); load(); };

  if (reviews.length === 0)
    return <p data-testid="reviews-empty" className="py-10 text-center text-sm text-white/40">No reviews yet. Approved reviews appear on the homepage and power your Google star rating.</p>;

  return (
    <div data-testid="reviews-list" className="space-y-3">
      {reviews.map((r) => (
        <div key={r.id} data-testid="review-row" className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#171F26] p-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">{r.name}</p>
              <span className="flex gap-0.5">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className={`h-3 w-3 ${s < r.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"}`} />
                ))}
              </span>
            </div>
            <p className="mt-1 text-sm text-white/55">"{r.text}"</p>
            <p className="mt-1 text-xs text-white/40">{[r.from_place, r.trip].filter(Boolean).join(" · ")}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid={`review-approve-${r.id}`}
              onClick={() => approve(r.id, !r.approved)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium ${r.approved ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-400" : "border-amber-500/30 bg-amber-500/15 text-amber-400"}`}
            >
              {r.approved ? "Published" : "Pending — approve"}
            </button>
            <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)} aria-label="Delete review" className="text-white/30 transition-colors hover:text-red-400">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function VehiclesTab() {
  const EMPTY = { name: "", category: "suv", seats: "", rate: "", image: "", alt: "" };
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (editId) await api.put(`/vehicles/${editId}`, form).catch(() => {});
    else await api.post("/vehicles", form).catch(() => {});
    setForm(EMPTY); setEditId(null); setShowForm(false); load();
  };
  const edit = (v) => { setForm({ name: v.name, category: v.category, seats: v.seats, rate: v.rate, image: v.image, alt: v.alt || "" }); setEditId(v.id); setShowForm(true); };
  const remove = async (id) => { await api.delete(`/vehicles/${id}`).catch(() => {}); load(); };

  return (
    <div>
      <button data-testid="vehicle-add-btn" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(!showForm); }} className="mb-5 flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-sm text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0F1418]">
        {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {showForm ? "Close" : "Add vehicle"}
      </button>
      {showForm && (
        <form onSubmit={save} data-testid="vehicle-form" className="mb-6 grid gap-3 rounded-2xl border border-white/10 bg-[#171F26] p-5 sm:grid-cols-2">
          <input required placeholder="Name (e.g. Toyota Innova)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="vehicle-name-input" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="vehicle-category-select">
            <option value="suv">Premium SUV</option><option value="sedan">Sedan</option><option value="tempo">Tempo Traveller</option><option value="hatch">Hatchback</option>
          </select>
          <input required placeholder="Seats (e.g. 7 seats)" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} className={inputCls} />
          <input required placeholder="Rate (e.g. ₹18/km)" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className={inputCls} />
          <input required placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={`${inputCls} sm:col-span-2`} />
          <button type="submit" data-testid="vehicle-save-btn" className="rounded-full bg-[#D4AF37] px-6 py-2.5 text-sm font-semibold text-[#0F1418] sm:col-span-2">
            {editId ? "Save changes" : "Add vehicle"}
          </button>
        </form>
      )}
      <div data-testid="vehicles-list" className="space-y-3">
        {vehicles.map((v) => (
          <div key={v.id} data-testid="vehicle-row" className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#171F26] p-5">
            <div>
              <p className="font-medium">{v.name} <span className="text-xs text-[#D4AF37]">{v.rate}</span></p>
              <p className="mt-0.5 text-xs text-white/45">{v.category} · {v.seats}</p>
            </div>
            <div className="flex gap-3">
              <button data-testid={`vehicle-edit-${v.id}`} onClick={() => edit(v)} aria-label={`Edit ${v.name}`} className="text-white/40 transition-colors hover:text-[#D4AF37]"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`vehicle-delete-${v.id}`} onClick={() => remove(v.id)} aria-label={`Delete ${v.name}`} className="text-white/30 transition-colors hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PackagesTab() {
  const EMPTY = { slug: "", name: "", duration: "", image: "", alt: "", points: "", includes: "", itinerary: "" };
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get("/packages").then((r) => setPackages(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const toPayload = (f) => ({
    slug: f.slug.trim(), name: f.name, duration: f.duration, image: f.image, alt: f.alt,
    points: f.points.split("\n").map((s) => s.trim()).filter(Boolean),
    includes: f.includes.split("\n").map((s) => s.trim()).filter(Boolean),
    itinerary: f.itinerary.split("\n").map((s) => s.trim()).filter(Boolean).map((line, i) => {
      const [title, ...rest] = line.split("|");
      return { day: i + 1, title: title.trim(), detail: rest.join("|").trim() };
    }),
  });

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editId) await api.put(`/packages/${editId}`, toPayload(form));
      else await api.post("/packages", toPayload(form));
      setForm(EMPTY); setEditId(null); setShowForm(false); load();
    } catch (err) { setError(fmtErr(err)); }
  };
  const edit = (p) => {
    setForm({
      slug: p.slug, name: p.name, duration: p.duration, image: p.image, alt: p.alt || "",
      points: (p.points || []).join("\n"), includes: (p.includes || []).join("\n"),
      itinerary: (p.itinerary || []).map((d) => `${d.title} | ${d.detail}`).join("\n"),
    });
    setEditId(p.id); setShowForm(true);
  };
  const remove = async (id) => { await api.delete(`/packages/${id}`).catch(() => {}); load(); };

  return (
    <div>
      <button data-testid="package-add-btn" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(!showForm); }} className="mb-5 flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-sm text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0F1418]">
        {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {showForm ? "Close" : "Add package"}
      </button>
      {showForm && (
        <form onSubmit={save} data-testid="package-form" className="mb-6 grid gap-3 rounded-2xl border border-white/10 bg-[#171F26] p-5 sm:grid-cols-2">
          <input required placeholder="Package name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="package-name-input" />
          <input required placeholder="url-slug (e.g. puri-konark-heritage)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={inputCls} data-testid="package-slug-input" />
          <input required placeholder="Duration (e.g. 3 Days · 2 Nights)" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className={inputCls} />
          <input required placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} />
          <textarea rows="3" placeholder="Highlights — one per line" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className={`${inputCls} sm:col-span-2`} />
          <textarea rows="3" placeholder="What's included — one per line" value={form.includes} onChange={(e) => setForm({ ...form, includes: e.target.value })} className={`${inputCls} sm:col-span-2`} />
          <textarea rows="4" placeholder={"Itinerary — one day per line, as:\nDay title | What happens that day"} value={form.itinerary} onChange={(e) => setForm({ ...form, itinerary: e.target.value })} className={`${inputCls} sm:col-span-2`} />
          {error && <p data-testid="package-form-error" className="text-sm text-red-300 sm:col-span-2">{error}</p>}
          <button type="submit" data-testid="package-save-btn" className="rounded-full bg-[#D4AF37] px-6 py-2.5 text-sm font-semibold text-[#0F1418] sm:col-span-2">
            {editId ? "Save changes" : "Add package"}
          </button>
        </form>
      )}
      <div data-testid="packages-list" className="space-y-3">
        {packages.map((p) => (
          <div key={p.id} data-testid="package-row" className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#171F26] p-5">
            <div>
              <p className="font-medium">{p.name}</p>
              <p className="mt-0.5 text-xs text-white/45">/packages/{p.slug} · {p.duration}</p>
            </div>
            <div className="flex gap-3">
              <button data-testid={`package-edit-${p.id}`} onClick={() => edit(p)} aria-label={`Edit ${p.name}`} className="text-white/40 transition-colors hover:text-[#D4AF37]"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`package-delete-${p.id}`} onClick={() => remove(p.id)} aria-label={`Delete ${p.name}`} className="text-white/30 transition-colors hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PromotionsTab() {
  const EMPTY = { title: "", description: "", active: true };
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/promotions/all").then((r) => setPromos(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    await api.post("/promotions", form).catch(() => {});
    setForm(EMPTY); setShowForm(false); load();
  };
  const toggle = async (id, active) => { await api.patch(`/promotions/${id}`, { active }).catch(() => {}); load(); };
  const remove = async (id) => { await api.delete(`/promotions/${id}`).catch(() => {}); load(); };

  return (
    <div>
      <button data-testid="promo-add-btn" onClick={() => setShowForm(!showForm)} className="mb-5 flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-sm text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0F1418]">
        {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {showForm ? "Close" : "Add promotion"}
      </button>
      {showForm && (
        <form onSubmit={save} data-testid="promo-form" className="mb-6 grid gap-3 rounded-2xl border border-white/10 bg-[#171F26] p-5">
          <input required placeholder="Offer title (e.g. Winter Darshan Special)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="promo-title-input" />
          <input placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
          <button type="submit" data-testid="promo-save-btn" className="rounded-full bg-[#D4AF37] px-6 py-2.5 text-sm font-semibold text-[#0F1418]">Publish offer</button>
        </form>
      )}
      {promos.length === 0 && !showForm && (
        <p data-testid="promos-empty" className="py-10 text-center text-sm text-white/40">No promotions yet. Active promotions show as a gold banner on the homepage.</p>
      )}
      <div data-testid="promos-list" className="space-y-3">
        {promos.map((p) => (
          <div key={p.id} data-testid="promo-row" className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#171F26] p-5">
            <div>
              <p className="font-medium">{p.title}</p>
              {p.description && <p className="mt-0.5 text-xs text-white/45">{p.description}</p>}
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid={`promo-toggle-${p.id}`}
                onClick={() => toggle(p.id, !p.active)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium ${p.active ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-400" : "border-white/15 text-white/40"}`}
              >
                {p.active ? "Live" : "Hidden"}
              </button>
              <button data-testid={`promo-delete-${p.id}`} onClick={() => remove(p.id)} aria-label="Delete promotion" className="text-white/30 transition-colors hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("Bookings");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user === null) navigate("/admin/login");
  }, [user, navigate]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!user) return <div className="flex min-h-screen items-center justify-center text-white/40">Loading…</div>;

  return (
    <div data-testid="admin-dashboard" className="mx-auto min-h-screen max-w-5xl px-4 py-10 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#D4AF37]">Jay Jagannath Travels</p>
          <h1 className="mt-2 font-serif text-3xl font-semibold">Dashboard</h1>
          <p className="mt-1 text-xs text-white/40">Signed in as {user.email}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/" data-testid="admin-view-site" className="rounded-full border border-white/15 px-4 py-2 text-sm text-white/60 transition-colors hover:border-[#D4AF37] hover:text-white">View site</Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/60 transition-colors hover:border-red-400 hover:text-red-300">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10 pb-4">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t.toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`rounded-full px-4 py-2 text-sm transition-colors ${tab === t ? "bg-[#D4AF37] font-semibold text-[#0F1418]" : "text-white/55 hover:text-white"}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="py-8">
        {tab === "Bookings" && <BookingsTab />}
        {tab === "Reviews" && <ReviewsTab />}
        {tab === "Vehicles" && <VehiclesTab />}
        {tab === "Packages" && <PackagesTab />}
        {tab === "Promotions" && <PromotionsTab />}
      </div>
    </div>
  );
}
